import { TaxonomyFacet, TaxonomyTermStatus } from '@edgebox/sync-core-data-definitions';
import { ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import { TaxonomyTermIndex, useTaxonomyTermIndex } from './taxonomyTermIndex';
import { ITaxonomyTermFacetSource } from './useCachedTaxonomyData';
import { FacetConfig, findConfiguredTerm } from '../../../GovernanceAnalyticsAiVisibility.utils';
import {
  faUsers,
  faFilter,
  faLayerGroup,
  faRocket,
  faShieldHalved,
  faSpinner,
  faTriangleExclamation,
  faWrench,
  faSeal,
  faEarthAmericas,
  faArrowsDownToPeople,
  faBoxOpen,
  faBrain,
  faRobot,
  faLightbulb,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { BP_TAG_CHIP_CLASSES } from '../../../Components/brandPresenceClasses';
import { EngineLogo, hasEngineLogo } from '../../../Components/EngineLogo';
import { SUGGESTED_TERM_HINT, TermRenderContext, termStatusPresentation } from '../../../Components/termStatusPresentation';

export type TermBadgePresentation = {
  color: string;
  icon: typeof faFilter;
};

type TaxonomyTermLoadResult = {
  name: string;
  taxonomyFacet?: TaxonomyFacet;
  /**
   * The status the term resolved at. The lookup below already walks the statuses in order, so the
   * matching one is known for free — keeping it is what lets every surface present a term by its
   * curation state without a second request or a payload change.
   */
  status?: TaxonomyTermStatus;
  /** The term's stored brand colour, when it carries one; read from the same index entry. */
  color?: string;
};

const termBadgePresentationByFacet: Partial<Record<TaxonomyFacet, TermBadgePresentation>> = {
  [TaxonomyFacet.Brand]: { color: '#5A00D6', icon: faSeal },
  [TaxonomyFacet.Initiative]: { color: '#007B6E', icon: faRocket },
  [TaxonomyFacet.Product]: { color: '#1F2A8A', icon: faBoxOpen },
  [TaxonomyFacet.Market]: { color: '#1B8F2E', icon: faEarthAmericas },
  [TaxonomyFacet.Persona]: { color: '#B000B5', icon: faArrowsDownToPeople },
  [TaxonomyFacet.FunnelStage]: { color: '#0057FF', icon: faFilter },
  [TaxonomyFacet.Intent]: { color: '#475569', icon: faUsers },
  [TaxonomyFacet.AuthorityTopic]: { color: '#0081A7', icon: faBrain },
  [TaxonomyFacet.ContentType]: { color: '#8AA300', icon: faLayerGroup },
  [TaxonomyFacet.ComplianceStandard]: { color: '#00406E', icon: faShieldHalved },
  [TaxonomyFacet.IssueType]: { color: '#4B5563', icon: faWrench },
  [TaxonomyFacet.Incident]: { color: '#D97706', icon: faTriangleExclamation },
  [TaxonomyFacet.Engine]: { color: '#4338CA', icon: faRobot },
};

export function resolveTaxonomyFacetPresentation(facet: TaxonomyFacet): TermBadgePresentation | undefined {
  return termBadgePresentationByFacet[facet];
}

function resolveTermBadgePresentation(termFacet: TaxonomyFacet | undefined): TermBadgePresentation | undefined {
  if (!termFacet) {
    return undefined;
  }

  return resolveTaxonomyFacetPresentation(termFacet);
}

/**
 * One term's name and status, read from the shared index.
 *
 * Every lookup below answers from that one fetch. There is deliberately NO per-key fallback
 * request: a key the loaded index does not carry is missing, and asking the single-term endpoint
 * about it is precisely the fan-out that took the analytics page down.
 */
export function resolveTermFromIndex(index: TaxonomyTermIndex | undefined, termKey: string): TaxonomyTermLoadResult | undefined {
  const entry = index?.byKey.get(termKey.trim());
  return entry ? { name: entry.name, taxonomyFacet: entry.taxonomyFacet, status: entry.status, color: entry.color } : undefined;
}

/**
 * Resolves a term key to its display name from the shared index — so a page naming two hundred
 * terms still costs the one fetch, however many components render them.
 *
 * Returns the raw key while loading and when the term does not resolve, so a name is never rendered
 * as blank.
 */
function useTaxonomyTermName(api: ITaxonomyTermFacetSource | undefined, termKey: string, termFacet?: TaxonomyFacet) {
  const { index, loading } = useTaxonomyTermIndex(api);
  const cacheKey = termKey.trim();
  const resolved = resolveTermFromIndex(index, cacheKey);

  // Unchanged display contract: while the index is in flight the caller renders a spinner, and a
  // key that does not resolve keeps falling back to itself rather than rendering blank.
  return {
    termName: resolved?.name ?? (termKey || 'Term'),
    loadedTermFacet: resolved?.taxonomyFacet ?? termFacet,
    termStatus: resolved?.status,
    loading: loading && !!cacheKey,
  };
}

export { useTaxonomyTermName };

/**
 * The plain, un-badged display name of a taxonomy term. Use this where the term *is* the
 * subject of the row (e.g. the first column of a table): a badge there adds visual noise
 * without adding information, and the surrounding table already provides the context a badge
 * would otherwise carry.
 */
export function TaxonomyTermName({
  api,
  termKey,
  className,
  context = 'name',
  neverMute,
}: {
  api?: ISyncCoreApi;
  termKey: string;
  className?: string;
  /** Where this name is rendered; decides what muting looks like. See `termStatusPresentation`. */
  context?: TermRenderContext;
  /** Set on the recommendation/winner display, which reports a finding rather than a taxonomy state. */
  neverMute?: boolean;
}) {
  const { termName, termStatus, loading } = useTaxonomyTermName(api, termKey);
  const presentation = termStatusPresentation(termStatus, context, { neverMute });

  // An archived term is not part of what the customer is looking at. Collections filter archived
  // rows out before rendering; this is the backstop for a single name rendered on its own.
  if (presentation.hidden) {
    return null;
  }

  return (
    <span className={[className, presentation.className].filter(Boolean).join(' ')} title={presentation.title ?? termName}>
      {loading ? <FontAwesomeIcon icon={faSpinner} spin className="animate-spin" style={{ animationDuration: '2.2s' }} /> : termName}
      {presentation.showSuggestionIcon ? <SuggestedTermIcon /> : null}
    </span>
  );
}

/**
 * The plain, un-badged display name of a taxonomy term, resolved from a `FacetConfig[]` list — the
 * page's own facet-scoped fetches (`getCachedTaxonomyTermsByFacet`) — rather than the shared term
 * index.
 *
 * Same contract as `TaxonomyTermName` above (falls back to the raw key rather than rendering blank,
 * applies the same status presentation), but for a facet a screen has already loaded for its own
 * filter UI or `facetConfigs`-style lists: asking the index for that facet too would fetch the same
 * terms a second time. Use `TaxonomyTermName` for a facet nothing else on the screen already fetched.
 */
export function FacetConfiguredTermName({
  facetConfigs,
  termKey,
  className,
  context = 'name',
  neverMute,
}: {
  facetConfigs: FacetConfig[];
  termKey: string;
  className?: string;
  context?: TermRenderContext;
  neverMute?: boolean;
}) {
  const configured = findConfiguredTerm(facetConfigs, termKey);
  const presentation = termStatusPresentation(configured?.status, context, { neverMute });
  const name = configured?.name ?? termKey;

  // An archived term is not part of what the customer is looking at — the same backstop
  // `TaxonomyTermName` applies for the index-backed facets.
  if (presentation.hidden) {
    return null;
  }

  return (
    <span className={[className, presentation.className].filter(Boolean).join(' ')} title={presentation.title ?? name}>
      {name}
      {presentation.showSuggestionIcon ? <SuggestedTermIcon /> : null}
    </span>
  );
}

/**
 * The lightbulb that marks a term as a suggestion, rendered after the name.
 *
 * `spacing` exists because the icon carries its own left margin. That is right when it trails a name
 * in running text, and wrong inside a flex row that already spaces its children — there the margin
 * and the gap add up and the bulb drifts away from the label it belongs to. `flush` drops the margin
 * and lets the container's own gap do the spacing. Default unchanged, so no existing call site moves.
 */
export function SuggestedTermIcon({ className, spacing = 'inline' }: { className?: string; spacing?: 'inline' | 'flush' }) {
  return (
    <FontAwesomeIcon
      icon={faLightbulb}
      className={[spacing === 'inline' ? 'ms-1' : '', className].filter(Boolean).join(' ')}
      title={SUGGESTED_TERM_HINT}
      aria-label={SUGGESTED_TERM_HINT}
    />
  );
}

export function TaxonomyTermNameBadge({
  api,
  termKey,
  applyFacetPresentation,
  termFacet,
  onRemove,
  neverMute,
}: {
  api?: ISyncCoreApi;
  termKey: string;
  applyFacetPresentation?: boolean;
  termFacet?: TaxonomyFacet;
  onRemove?: () => void;
  /** Set where the badge reports a finding rather than a taxonomy state (see the winner exception). */
  neverMute?: boolean;
}) {
  const { termName, loadedTermFacet, termStatus, loading } = useTaxonomyTermName(api, termKey, termFacet);
  const presentation = React.useMemo(
    () => (applyFacetPresentation ? resolveTermBadgePresentation(termFacet || loadedTermFacet) : undefined),
    [applyFacetPresentation, termFacet, loadedTermFacet]
  );
  // Every badge in the app renders through here, so applying the status treatment once covers them
  // all rather than leaving each screen to remember.
  const statusPresentation = termStatusPresentation(termStatus, 'badge', { neverMute });

  const labelNode = loading ? (
    <FontAwesomeIcon icon={faSpinner} spin className="animate-spin" style={{ animationDuration: '2.2s' }} />
  ) : (
    <span className="min-w-0 break-words">{termName}</span>
  );

  // An archived term is not part of what the customer is looking at.
  if (statusPresentation.hidden) {
    return null;
  }

  const suggestionIcon = statusPresentation.showSuggestionIcon ? <SuggestedTermIcon /> : null;

  // An engine is recognised by its mark well before its name is read, so where this app carries the
  // mark the mark IS the badge: the pill and the generic robot icon go away and the name becomes the
  // tooltip. No spinner on this path — the logo is decided by the key the caller already holds, so
  // there is nothing to wait for; only the name it is titled with comes from the index, and that
  // falls back to the key exactly as every other label here does.
  //
  // An engine with no logo file, and every other facet, falls through to the badges below unchanged.
  if (hasEngineLogo(termKey)) {
    return (
      <span
        title={statusPresentation.title ?? termName}
        className={['inline-flex items-center gap-1', statusPresentation.className].filter(Boolean).join(' ')}
      >
        <EngineLogo termKey={termKey} name={termName} size="badge" />
        {statusPresentation.showSuggestionIcon ? <SuggestedTermIcon spacing="flush" /> : null}
        {onRemove ? (
          <button
            type="button"
            className="rounded border-0 bg-transparent px-1 py-0 text-slate-500 hover:text-slate-700"
            onClick={onRemove}
          >
            X
          </button>
        ) : null}
      </span>
    );
  }

  if (!presentation) {
    return (
      <span
        title={statusPresentation.title ?? termName}
        className={[BP_TAG_CHIP_CLASSES, statusPresentation.className].filter(Boolean).join(' ')}
      >
        {labelNode}
        {suggestionIcon}
        {onRemove ? (
          <button
            type="button"
            className="ml-1 rounded border-0 bg-transparent px-1 py-0 text-slate-500 hover:text-slate-700"
            onClick={onRemove}
          >
            X
          </button>
        ) : null}
      </span>
    );
  }

  return (
    <span
      title={statusPresentation.title ?? termName}
      className={[
        'inline-flex max-w-full items-center gap-1 rounded-xl border px-2.5 py-0.5 text-[0.6875rem] font-semibold leading-5 break-words',
        statusPresentation.className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        color: presentation.color,
        backgroundColor: `${presentation.color}1A`,
        borderColor: `${presentation.color}66`,
      }}
    >
      <FontAwesomeIcon icon={presentation.icon} className="text-[9px]" />
      {labelNode}
      {suggestionIcon}
      {onRemove ? (
        <button
          type="button"
          className="ml-1 rounded border-0 bg-transparent px-1 py-0 hover:opacity-80"
          style={{ color: presentation.color }}
          onClick={onRemove}
        >
          X
        </button>
      ) : null}
    </span>
  );
}

/**
 * Resolve the curation status of many terms at once, through the same module cache single-name
 * lookups use — so a key already rendered on the page costs nothing extra here.
 *
 * Needed where a term's status has to affect something other than its own label: the answer body's
 * highlight tints are keyed by term, but the tint is painted far from any name, so the colour has
 * to be muted before it ever reaches the painter.
 */
export function useTermStatuses(
  api: ITaxonomyTermFacetSource | undefined,
  termKeys: readonly string[]
): Map<string, TaxonomyTermStatus | undefined> {
  const { index } = useTaxonomyTermIndex(api);
  // Stable across renders unless the set actually changes, so the map is not rebuilt per render.
  const joinedKeys = termKeys.filter(Boolean).join(' ');

  return React.useMemo(() => {
    const statuses = new Map<string, TaxonomyTermStatus | undefined>();
    if (!index || !joinedKeys) {
      return statuses;
    }

    for (const key of joinedKeys.split(' ')) {
      const resolved = resolveTermFromIndex(index, key);
      // A term the index does not carry is left without a status, which reads as full strength.
      // Muting or hiding on an unresolved key would be a worse lie than showing it plainly.
      if (resolved) {
        statuses.set(key, resolved.status);
      }
    }

    return statuses;
  }, [index, joinedKeys]);
}

/**
 * The stored brand colour of each of the given term keys, read from the same shared index as
 * `useTermStatuses` — so the colours ride the one fetch the page already makes rather than a
 * second request.
 *
 * Only keys the index carries with a colour appear in the map; a key with no stored colour is
 * absent, which the painter reads as "fall back to the palette".
 */
export function useTermColors(api: ITaxonomyTermFacetSource | undefined, termKeys: readonly string[]): ReadonlyMap<string, string> {
  const { index } = useTaxonomyTermIndex(api);
  const joinedKeys = termKeys.filter(Boolean).join(' ');

  return React.useMemo(() => {
    const colors = new Map<string, string>();
    if (!index || !joinedKeys) {
      return colors;
    }

    for (const key of joinedKeys.split(' ')) {
      const resolved = resolveTermFromIndex(index, key);
      if (resolved?.color) {
        colors.set(key, resolved.color);
      }
    }

    return colors;
  }, [index, joinedKeys]);
}

/** What resolving one term key told us, for deciding whether its row may render. */
export type TermResolution =
  | { state: 'loading' }
  // The name rides along because it was already fetched: every consumer that needs a display
  // string would otherwise re-request the same term, or fall back to rendering the raw key.
  | { state: 'resolved'; status?: TaxonomyTermStatus; name?: string }
  | { state: 'missing' };

/**
 * Resolve many term keys and decide which of their ROWS may render.
 *
 * Stats endpoints aggregate over every term status by design — the server keeps archived rows so
 * that un-archiving a term immediately surfaces its history. The view is what filters. Rendering
 * a name through `TaxonomyTermName` is not enough on its own: it correctly returns nothing for an
 * archived term, which leaves the ROW in place with an empty name. So the row has to be dropped
 * where it is built, and that needs the status before the row renders.
 *
 * Three outcomes, deliberately distinguished:
 *  - loading — nothing is known yet, so rows stay (their names show their own spinner). The same
 *    module cache backs both, so a row and its name settle together and no blank row is shown.
 *  - resolved — the status decides, through the shared status→presentation helper.
 *  - missing — the term could not be resolved at ANY status (deleted). Its row is dropped too:
 *    a row that cannot name its own subject says nothing.
 */
export function useTermRowVisibility(api: ITaxonomyTermFacetSource | undefined, termKeys: readonly string[]) {
  const { index, loading, failed } = useTaxonomyTermIndex(api);
  const joinedKeys = Array.from(new Set(termKeys.filter(Boolean))).join(' ');

  const resolutions = React.useMemo(() => {
    const next = new Map<string, TermResolution>();
    const keys = joinedKeys ? joinedKeys.split(' ') : [];
    if (!api || !keys.length) {
      return next;
    }

    for (const key of keys) {
      if (loading) {
        next.set(key, { state: 'loading' });
        continue;
      }

      // An index that could not be loaded says nothing about any term, so no row is judged by it.
      // Leaving these unresolved keeps the rows on screen — erasing every row of a page because one
      // request failed would turn a loading problem into a data-loss illusion.
      if (failed || !index) {
        continue;
      }

      const resolved = resolveTermFromIndex(index, key);
      next.set(key, resolved ? { state: 'resolved', status: resolved.status, name: resolved.name } : { state: 'missing' });
    }

    return next;
  }, [api, joinedKeys, index, loading, failed]);

  return React.useMemo(() => {
    const statusOf = (termKey: string): TaxonomyTermStatus | undefined => {
      const resolution = resolutions.get(termKey);
      return resolution?.state === 'resolved' ? resolution.status : undefined;
    };

    /** The curated display name, or undefined while loading / when the term does not resolve. */
    const nameOf = (termKey: string): string | undefined => {
      const resolution = resolutions.get(termKey);
      return resolution?.state === 'resolved' ? resolution.name : undefined;
    };

    return {
      statusOf,
      nameOf,
      /** Whether this term's row may be rendered at all. */
      isRowVisible: (termKey: string): boolean => {
        const resolution = resolutions.get(termKey);
        // Unknown key or still loading: keep the row; its name renders a spinner meanwhile.
        if (!resolution || resolution.state === 'loading') {
          return true;
        }
        if (resolution.state === 'missing') {
          return false;
        }
        return !termStatusPresentation(resolution.status, 'table-row').hidden;
      },
    };
  }, [resolutions]);
}
